import React, { useState, useEffect } from 'react';
import { fetchSubCountyAnalysis } from '../services/analysisService';
import AchievementBar from '../components/AchievementBar';
import { calculatePerformance, getHeatmapClass } from '../utils/analysisUtils';
import { ChevronRight, ChevronDown } from 'lucide-react';

const SubCountyComparisonTab = ({ subCountyId, fyStart, data, loading }) => {
  const [expanded, setExpanded] = useState({});

  
  // 1. Calculate Grand Totals across all streams
    const grandTotals = Object.values(data).flat().reduce((acc, curr) => {
      acc.fy0 += curr.amount_fy0 || 0;
      acc.fyMinus1 += curr.amount_fy_minus_1 || 0;
      acc.fyMinus2 += curr.amount_fy_minus_2 || 0;
      return acc;
    }, { fy0: 0, fyMinus1: 0, fyMinus2: 0 });

    // 2. Sum up unique stream targets
    const totalTarget = Object.values(data).reduce((sum, months) => 
      sum + (months.find(m => m.annual_target > 0)?.annual_target || 0), 0);

    const totalPerf = calculatePerformance(grandTotals.fy0, totalTarget);
/*
    // 3. Prepare Monthly Summary for the Sub-County
    const monthlyGrandTotals = Array.from({ length: 12 }, (_, i) => {
      const monthsInOrder = ['July', 'August', 'September', 'October', 'November', 'December', 'January', 'February', 'March', 'April', 'May', 'June'];
      const monthName = monthsInOrder[i];
      
      return Object.values(data).flat()
        .filter(item => item.calendar_month === monthName)
        .reduce((acc, curr) => {
          acc.month = monthName;
          acc.fy0 += curr.amount_fy0 || 0;
          acc.fyMinus1 += curr.amount_fy_minus_1 || 0;
          acc.fyMinus2 += curr.amount_fy_minus_2 || 0;
          return acc;
        }, { month: monthName, fy0: 0, fyMinus1: 0, fyMinus2: 0 });
    });
  */
// Add target calculation to the monthlyGrandTotals array in both components
const monthlyGrandTotals = Array.from({ length: 12 }, (_, i) => {
  const monthsInOrder = ['July', 'August', 'September', 'October', 'November', 'December', 'January', 'February', 'March', 'April', 'May', 'June'];
  const monthName = monthsInOrder[i];
  const targetKey = `${monthName.substring(0, 3).toLowerCase()}_target`;
  
  return Object.values(data).flat()
    .filter(item => item.calendar_month === monthName)
    .reduce((acc, curr) => {
      acc.month = monthName;
      acc.actual += curr.amount_fy0 || 0;
      acc.target += curr.monthly_target || 0; // Sum up targets for all streams
      acc.fyMinus1 += curr.amount_fy_minus_1 || 0;
      acc.fyMinus2 += curr.amount_fy_minus_2 || 0;
      // ... include fyMinus1/2 for ComparisonTab if needed
      return acc;
    }, { month: monthName, actual: 0, target: 0, fyMinus1: 0, fyMinus2: 0 });
  });
  if (loading) return <div className="p-10 text-center text-gray-400">Loading comparison matrix...</div>;

  return (
    <div className="bg-white rounded-xl border shadow-lg overflow-hidden">
      <table className="w-full text-sm text-left">
        <thead className="bg-gray-50 text-xs uppercase font-bold text-gray-600 border-b">
          <tr>
            <th className="px-6 py-4">Revenue Stream</th>
            <th className="px-4 py-4 italic">FY {fyStart-2}/{fyStart-1}</th>
            <th className="px-4 py-4 italic">FY {fyStart-1}/{fyStart}</th>
            <th className="px-4 py-4 text-blue-800">Current FY {fyStart}/{fyStart+1}</th>
            <th className="px-4 py-4 text-center">Achievement against Current Target</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(data).map(([stream, months]) => {
            const actual = months.reduce((s, m) => s + m.amount_fy0, 0);
            // Explicitly find a row that has the target, or default to 0
            const target = months.find(m => m.annual_target > 0)?.annual_target || 0;
            //const target = months[0]?.annual_target || 0;
            const perf = calculatePerformance(actual, target);
            const isExpanded = expanded[stream];

            return (
              <React.Fragment key={stream}>
                <tr className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => setExpanded(p => ({...p, [stream]: !p[stream]}))}>
                  <td className="px-6 py-4 font-bold flex items-center">
                    {isExpanded ? <ChevronDown size={16} className="mr-2 text-blue-500" /> : <ChevronRight size={16} className="mr-2" />}
                    {stream}
                  </td>
                  <td className="px-4 py-4 font-bold text-purple-400">{months.reduce((s, m) => s + m.amount_fy_minus_2, 0).toLocaleString()}</td>
                  <td className="px-4 py-4 font-bold text-purple-400">{months.reduce((s, m) => s + m.amount_fy_minus_1, 0).toLocaleString()}</td>
                  <td className="px-4 py-4 font-bold text-blue-700">
                    {actual.toLocaleString()}
                    <AchievementBar actual={actual} target={target} />
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getHeatmapClass(perf)}`}>{perf.toFixed(1)}%</span>
                  </td>
                </tr>
                {/* Inside SubCountyComparisonTab expansion logic */}
                {isExpanded && months.map((m, i) => {
                  // Map the calendar month name to the target key in the database
                  const targetKey = `${m.calendar_month.substring(0, 3).toLowerCase()}_target`;
                  const monthlyTarget = m.monthly_target || 0;
                  return (
                    <tr key={i} className="bg-gray-50/50 text-xs italic border-b">
                      <td className="px-12 py-2 text-gray-600 font-medium">{m.calendar_month}</td>
                      <td className="px-4 py-2 text-purple-500 font-medium">{m.amount_fy_minus_2.toLocaleString()}</td>
                      <td className="px-4 py-2 text-purple-500 font-medium">{m.amount_fy_minus_1.toLocaleString()}</td>
                      <td className="px-4 py-2 text-blue-600 font-medium">
                        {m.amount_fy0.toLocaleString()} 
                        <AchievementBar actual={m.amount_fy0} target={monthlyTarget} />
                      </td>
                      <td className="text-center">
                        {/* Performance vs Monthly Target */}
                        <span className="text-[10px] font-bold text-gray-500">
                          {monthlyTarget > 0 ? ((m.amount_fy0 / monthlyTarget) * 100).toFixed(1) + '%' : '-'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
        {/* ... existing tbody ... */}
        <tfoot className="border-t-4 border-blue-200">
          <tr 
            className="bg-blue-100 font-extrabold cursor-pointer hover:bg-blue-200 transition-colors"
            onClick={() => setExpanded(p => ({...p, grandTotal: !p.grandTotal}))}
          >
            <td className="px-6 py-5 flex items-center shadow-sm">
              {expanded.grandTotal ? <ChevronDown size={20} className="mr-2 text-blue-700" /> : <ChevronRight size={20} className="mr-2 text-blue-700" />}
              GRAND TOTAL (SUB-COUNTY)
            </td>
            <td className="px-4 py-5 text-purple-700">{grandTotals.fyMinus2.toLocaleString()}</td>
            <td className="px-4 py-5 text-purple-700">{grandTotals.fyMinus1.toLocaleString()}</td>
            <td className="px-4 py-5 text-blue-900">
              {grandTotals.fy0.toLocaleString()}
              <AchievementBar actual={grandTotals.fy0} target={totalTarget} />
            </td>
            <td className="px-4 py-5 text-center">
              <span className={`px-4 py-1.5 rounded-full text-sm font-black shadow-sm ${getHeatmapClass(totalPerf)}`}>
                {totalPerf.toFixed(1)}%
              </span>
            </td>
          </tr>

          {/* Monthly Summary Expansion */}
          {expanded.grandTotal && monthlyGrandTotals.map((m, idx) => (
            <tr key={`total-month-${idx}`} className="bg-blue-50/40 border-b text-xs font-bold italic text-blue-900/70">
              <td className="px-12 py-3 border-r shadow-sm">
                {m.month} (All Streams)
              </td>
              <td className="px-4 py-3">{m.fyMinus2.toLocaleString()}</td>
              <td className="px-4 py-3">{m.fyMinus1.toLocaleString()}</td>
              <td className="px-4 py-3 font-bold text-blue-700">{m.actual.toLocaleString()}
                <AchievementBar actual={m.actual} target={m.target} />
              </td>
              <td className="px-4 py-3 text-center">
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${getHeatmapClass(calculatePerformance(m.actual, m.target))}`}>
                {calculatePerformance(m.actual, m.target).toFixed(0)}%
                </span>
              </td>
            </tr>
          ))}
        </tfoot>
      </table>
    </div>
  );
};

export default SubCountyComparisonTab;